
import Vue from 'vue'
import { useFile } from '~/compositions/files/file'
/* eslint-disable no-console,no-undef */
export default Vue.extend({
  props: {
    maxResizeWidth: { type: Number, default: 2100 },
    maxResizeHeight: { type: Number, default: 1708 },
    minHeight: { type: Number, default: 0 },
    minWidth: { type: Number, default: 0 }
  },
  setup() {
    const { resizeAndReadImage } = useFile()
    return { resizeAndReadImage }
  },
  computed: {
    imageOrientationSupport() {
      const imgTag = document.createElement('img')
      return imgTag.style.imageOrientation !== undefined
    },
    isCanvasSupported() {
      const elem = document.createElement('canvas')
      return !!(elem.getContext && elem.getContext('2d'))
    }
  },
  methods: {
    realAddFile(origFile, currentFilesLength) {
      // THIS METHOD SHOULD BE OVERWRITTEN AT THE COMPONENT
      console.error('no realAddFile method found', origFile, currentFilesLength)
    },
    fileNotAnImage(origFile) {
      // THIS METHOD SHOULD BE OVERWRITTEN AT THE COMPONENT
      console.error('no fileNotAnImage method found', origFile)
    },
    async readFile(origFile, cFilesLength) {
      const currentFiles = cFilesLength
      const file = origFile.file

      const fileExtension = file.name.split('.').pop()
      const isHeic = ['heic', 'heif'].includes(fileExtension.toLowerCase())
      if (!file.type.match('image.*') || isHeic) {
        // this also catches heics without type
        if (isHeic) {
          const { photo, thumb, width, height } = await this.resizeAndReadImage(
            {
              file,
              maxResizeWidth: this.maxResizeWidth,
              maxResizeHeight: this.maxResizeHeight,
              exactOnly: false,
              minWidth: this.minWidth,
              minHeight: this.minHeight
            }
          )
          this.realAddFile(
            { ...origFile, file: photo, thumb, width, height },
            currentFiles
          )
          return
        }
        this.fileNotAnImage(origFile, currentFiles)
        return
      }

      if (!this.isCanvasSupported) {
        // if canvas is not supported add file as is
        this.realAddFile(origFile, currentFiles)
      }

      const MAX_WIDTH = this.maxResizeWidth
      const MAX_HEIGHT = this.maxResizeHeight
      const MIN_WIDTH = this.minWidth
      const MIN_HEIGHT = this.minHeight

      let orientation = -1

      const reader = new FileReader()
      const orientationReader = new FileReader()

      reader.onload = e => {
        if (file.type.match('image.*')) {
          origFile.thumb = e.target.result
          origFile.type = 'image'

          // start resize and orientation fix
          const origImg = new Image()

          origImg.onload = event => {
            const ewidth = event.target.width
            const eheight = event.target.height

            let width = ewidth
            let height = eheight

            if (orientation > 4 && orientation < 9) {
              width = event.target.height
              height = event.target.width
            }

            const canvas = document.createElement('canvas')

            // Don't resize if it's small enough
            if (
              width <= MAX_WIDTH &&
              height <= MAX_HEIGHT &&
              width >= MIN_WIDTH &&
              height >= MIN_HEIGHT &&
              orientation < 0
            ) {
              canvas.width = origImg.width
              canvas.height = origImg.height
              const ctx = canvas.getContext('2d')
              ctx.fillStyle = '#eeeeee' // background color for pngs
              ctx.fillRect(0, 0, canvas.width, canvas.height)
              ctx.drawImage(origImg, 0, 0, origImg.width, origImg.height)

              const canvasFile = this.base64ToFile(
                canvas.toDataURL('image/jpeg'),
                file
              )
              origFile.file = canvasFile
              origFile.thumb = canvas.toDataURL('image/jpeg')
              origFile.width = width
              origFile.height = height
              this.realAddFile(origFile, currentFiles)
              return
            }

            // Resize
            canvas.width = width
            canvas.height = height
            canvas.style.opacity = '0'

            let resized = false

            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width
              width = MAX_WIDTH
              resized = true
            }
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height
              height = MAX_HEIGHT
              resized = true
            }

            // repeat for minimum resizes
            if (width < MIN_WIDTH) {
              height *= MIN_WIDTH / width
              width = MIN_WIDTH
              resized = true
            }
            if (height < MIN_HEIGHT) {
              width *= MIN_HEIGHT / height
              height = MIN_HEIGHT
              resized = true
            }

            if (resized) {
              canvas.width = width
              canvas.height = height
            }

            try {
              // some browsers dont support canvas, if something here throws an error, we don't compress
              const ctx = canvas.getContext('2d')
              ctx.fillStyle = '#eeeeee' // background color for pngs
              ctx.fillRect(0, 0, canvas.width, canvas.height)

              // transform context before drawing image
              if (orientation > 1) {
                switch (orientation) {
                  case 2:
                    // flip horizontal
                    ctx.translate(width, 0)
                    ctx.scale(-1, 1)
                    ctx.drawImage(
                      origImg,
                      0,
                      0,
                      ewidth,
                      eheight,
                      0,
                      0,
                      width,
                      height
                    )
                    break // supported
                  case 3:
                    // rotate 180 degrees left
                    ctx.translate(width, height)
                    ctx.rotate(-Math.PI)
                    ctx.drawImage(
                      origImg,
                      0,
                      0,
                      ewidth,
                      eheight,
                      0,
                      0,
                      width,
                      height
                    )
                    break // supported
                  case 4:
                    // vertical flip
                    ctx.translate(0, height)
                    ctx.scale(1, -1)
                    ctx.drawImage(
                      origImg,
                      0,
                      0,
                      ewidth,
                      eheight,
                      0,
                      0,
                      width,
                      height
                    )
                    break // supported
                  case 5:
                    // 90 rotate right + flip horizontal
                    ctx.translate(width, 0)
                    ctx.scale(-1, 1)
                    ctx.translate(width, 0)
                    ctx.rotate((90 * Math.PI) / 180)

                    ctx.drawImage(
                      origImg,
                      0,
                      0,
                      ewidth,
                      eheight,
                      0,
                      0,
                      height,
                      width
                    )
                    break // supported
                  case 6:
                    // rotate 90 degrees right
                    ctx.translate(width, 0)
                    ctx.rotate((90 * Math.PI) / 180)
                    ctx.drawImage(
                      origImg,
                      0,
                      0,
                      ewidth,
                      eheight,
                      0,
                      0,
                      height,
                      width
                    )
                    break // supported
                  case 7:
                    // horizontal flip + 90 rotate right
                    ctx.translate(width, 0)
                    ctx.scale(-1, 1)
                    ctx.translate(0, height)
                    ctx.rotate((-90 * Math.PI) / 180)

                    ctx.drawImage(
                      origImg,
                      0,
                      0,
                      ewidth,
                      eheight,
                      0,
                      0,
                      height,
                      width
                    )
                    break
                  case 8:
                    ctx.translate(0, height)
                    ctx.rotate((-90 * Math.PI) / 180)
                    ctx.drawImage(
                      origImg,
                      0,
                      0,
                      ewidth,
                      eheight,
                      0,
                      0,
                      height,
                      width
                    )
                    break
                  default:
                    break
                }
              }

              // if it has orientation it will be drawn in the orientation case
              if (orientation <= 1) {
                if (resized) {
                  // ctx.drawImage(origImg, 0, 0, width, height);
                  ctx.drawImage(
                    origImg,
                    0,
                    0,
                    origImg.width,
                    origImg.height,
                    0,
                    0,
                    width,
                    height
                  )
                } else {
                  canvas.width = origImg.width
                  canvas.height = origImg.height
                  ctx.drawImage(origImg, 0, 0, origImg.width, origImg.height)
                }
              }

              const resizedFile = this.base64ToFile(
                canvas.toDataURL('image/jpeg'),
                file
              )

              origFile.file = resizedFile
              origFile.thumb = canvas.toDataURL('image/jpeg')
              origFile.width = width
              origFile.height = height
              this.realAddFile(origFile, currentFiles)
            } catch (e) {
              console.log('error resizing', e)
              origFile.width = width
              origFile.height = height
              this.realAddFile(origFile, currentFiles)
            }
          }
          origImg.src = e.target.result
        } else {
          this.fileNotAnImage(origFile, currentFiles)
        }
      }

      // reader.readAsDataURL(this.files[this.addIndx].file)

      orientationReader.onload = event => {
        if (!this.imageOrientationSupport) {
          const fileResult = event.target.result
          const view = new DataView(fileResult)

          if (view.getUint16(0, false) !== 0xffd8) {
            console.log('not jpeg!')
          } else {
            const length = view.byteLength
            let offset = 2
            while (offset < length) {
              if (view.getUint16(offset + 2, false) <= 8) {
                console.log('orientation not defined')
                orientation = -1
              }
              const marker = view.getUint16(offset, false)
              offset += 2
              if (marker === 0xffe1) {
                if (view.getUint32((offset += 2), false) !== 0x45786966) {
                  orientation = -1
                  break
                }

                const little = view.getUint16((offset += 6), false) === 0x4949
                offset += view.getUint32(offset + 4, little)
                const tags = view.getUint16(offset, little)
                offset += 2
                for (let i = 0; i < tags; i++) {
                  if (view.getUint16(offset + i * 12, little) === 0x0112) {
                    orientation = view.getUint16(offset + i * 12 + 8, little)
                    console.log('got orientation', orientation)
                  }
                }

                if (orientation > 0) {
                  break
                }
              } else if ((marker & 0xff00) !== 0xff00) {
                break
              } else {
                offset += view.getUint16(offset, false)
              }
            }
          }
        }

        // we got the orientation (if that exists), now lets check if it needs resize
        reader.readAsDataURL(file)
      }

      orientationReader.readAsArrayBuffer(file)
    },
    base64ToFile(dataURI, origFile) {
      let byteString

      if (dataURI.split(',')[0].includes('base64')) {
        byteString = atob(dataURI.split(',')[1])
      } else {
        byteString = decodeURI(dataURI.split(',')[1])
      }

      const mimestring = dataURI
        .split(',')[0]
        .split(':')[1]
        .split(';')[0]

      const content = []
      for (let i = 0; i < byteString.length; i++) {
        content[i] = byteString.charCodeAt(i)
      }

      const newFile = new File([new Uint8Array(content)], origFile.name, {
        type: mimestring
      })

      return newFile
    }
  }
})
